// prescription.js
// Owns: customer-account prescription rendering, per-card read-state, header
// notification dot, prescription cart-add actions.
//
// Loaded by layout/theme.liquid AFTER jquery.min.js and custom.js. Depends on:
//   - jQuery (`$`)
//   - window.getApiFetchArgs (assets/api-utils.js)
//   - AddQuizProductsToCart (assets/custom.js, top-level → globally accessible)
//
// Body data attributes consumed:
//   data-template, data-customer-email, data-customer-name, data-customer-id,
//   data-shop-url, data-prescription-read-state-enabled,
//   data-prescription-read-state-cutoff

(function () {
// ============================================================================
// Read-state storage (per-device, per-customer localStorage)
// ============================================================================
// Keyed by Shopify customer id so a shared device doesn't leak state across
// accounts. If localStorage is unavailable, every prescription is treated as
// already-viewed (graceful degrade — feature silently disabled rather than
// rendering perpetually-stuck dots).

function getPrescriptionViewedStorageKey() {
    var customerId = (document.body.getAttribute("data-customer-id") || "").trim();
    if (!customerId) return null;
    return "alumiermd.prescriptionsViewed." + customerId;
}

// Storage shape: { "<presId>": "<ISO-8601 timestamp>" | null }.
// Backward compat: the previous shape was a plain array of IDs without
// timestamps; reads tolerate either, writes always use the new shape.

function getViewedPrescriptionMap() {
    var key = getPrescriptionViewedStorageKey();
    if (!key) return null;
    try {
        var raw = window.localStorage.getItem(key);
        if (!raw) return {};
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            var migrated = {};
            for (var i = 0; i < parsed.length; i++) {
                migrated[String(parsed[i])] = null;
            }
            return migrated;
        }
        if (parsed && typeof parsed === 'object') {
            var clean = {};
            for (var k in parsed) {
                if (Object.prototype.hasOwnProperty.call(parsed, k)) {
                    var v = parsed[k];
                    clean[String(k)] = typeof v === 'string' ? v : null;
                }
            }
            return clean;
        }
        return {};
    } catch (e) {
        return null;
    }
}

function saveViewedPrescriptionMap(map) {
    var key = getPrescriptionViewedStorageKey();
    if (!key) return false;
    try {
        window.localStorage.setItem(key, JSON.stringify(map));
        return true;
    } catch (e) {
        return false;
    }
}

function getViewedPrescriptionIds() {
    var map = getViewedPrescriptionMap();
    if (map === null) return null;
    return new Set(Object.keys(map));
}

function recordPrescriptionViewed(prescriptionId) {
    if (!prescriptionId) return;
    var map = getViewedPrescriptionMap();
    if (map === null) return;
    map[String(prescriptionId)] = new Date().toISOString();
    saveViewedPrescriptionMap(map);
}

// `viewedMap` (optional) lets a render pass pass in a map it already read,
// so per-card lookups don't re-parse localStorage N times. Omit it to read fresh.
function getPrescriptionViewedTimestamp(prescriptionId, viewedMap) {
    if (!prescriptionId) return null;
    var map = viewedMap !== undefined ? viewedMap : getViewedPrescriptionMap();
    if (map === null) return null;
    return map[String(prescriptionId)] || null;
}

function isPrescriptionReadStateEnabled() {
    return document.body.getAttribute('data-prescription-read-state-enabled') === 'true';
}

// ============================================================================
// Prescriptions list cache (sessionStorage)
// ============================================================================
// Cache the /webtheme/theme/prescriptions response in sessionStorage so the
// header-dot path and the account-page renderer share a single fetch per
// 5-minute window per customer. Read-state (viewed IDs) lives in
// localStorage and is re-evaluated on each render, so dismissals stay
// accurate even while the list itself is cached.

var PRESCRIPTIONS_CACHE_TTL_MS = 5 * 60 * 1000;

function getPrescriptionsCacheKey() {
    var customerId = (document.body.getAttribute('data-customer-id') || '').trim();
    return customerId ? 'alumiermd.prescriptionsListCache.' + customerId : null;
}

function readCachedPrescriptionsList() {
    var key = getPrescriptionsCacheKey();
    if (!key) return null;
    try {
        var raw = window.sessionStorage.getItem(key);
        if (!raw) return null;
        var entry = JSON.parse(raw);
        if (!entry || typeof entry !== 'object') return null;
        if (Date.now() - (entry.t || 0) > PRESCRIPTIONS_CACHE_TTL_MS) return null;
        return Array.isArray(entry.list) ? entry.list : null;
    } catch (e) {
        return null;
    }
}

function writeCachedPrescriptionsList(list) {
    var key = getPrescriptionsCacheKey();
    if (!key) return;
    try {
        window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), list: list }));
    } catch (e) {}
}

// Shared in-flight fetch. On a cold cache the header-dot IIFE and
// updatePrescriptions() both run at bootstrap before either response lands;
// without this they'd each fire an identical Lambda call. Memoising the
// promise in module scope collapses them to one request. Resolves with the
// prescriptions list array (possibly empty), or null if the response wasn't
// usable; writes the sessionStorage cache on success.
var prescriptionsListPromise = null;

function getPrescriptionsListPromise(customerEmail) {
    if (prescriptionsListPromise) return prescriptionsListPromise;
    var cached = readCachedPrescriptionsList();
    if (cached) return Promise.resolve(cached);
    var endpoint =
        '/webtheme/theme/prescriptions?patientEmail=' + String(customerEmail).trim();
    var args = window.getApiFetchArgs(endpoint);
    prescriptionsListPromise = fetch(args.url, args.options)
        .then(function (r) { return r.json(); })
        .then(function (res) {
            if (res.message !== 'ok' || !res.prescriptions) return null;
            var list = Array.isArray(res.prescriptions)
                ? res.prescriptions
                : (Array.isArray(res.prescriptions.data) ? res.prescriptions.data : []);
            writeCachedPrescriptionsList(list);
            return list;
        });
    return prescriptionsListPromise;
}

// Theme-configurable cutoff: prescriptions created before this date are
// treated as already-viewed (no notification, no "Viewed on" date). Used
// to avoid retroactively flagging old prescriptions as unread when the
// read-state feature is first turned on. Returns a millisecond timestamp,
// or null if the setting is blank.
//
// Fail-closed: if the merchant sets an unparseable date (typo / wrong
// format), fall back to Date.now() so existing recommendations are still
// treated as viewed — rather than light up the notification dot on every
// patient's old prescription. Warn once so the misconfiguration is
// visible in the storefront preview console.
var _cutoffCache;
var _cutoffWarned = false;
function getPrescriptionReadStateCutoffMs() {
    if (_cutoffCache !== undefined) return _cutoffCache;
    var raw = (document.body.getAttribute('data-prescription-read-state-cutoff') || '').trim();
    if (!raw) {
        _cutoffCache = null;
        return _cutoffCache;
    }
    var ms = Date.parse(raw);
    if (isNaN(ms)) {
        if (!_cutoffWarned) {
            console.warn('[prescription.js] prescription_read_state_cutoff is set but unparseable: "' + raw + '". Falling back to "now" so existing recommendations stay marked as viewed.');
            _cutoffWarned = true;
        }
        _cutoffCache = Date.now();
        return _cutoffCache;
    }
    _cutoffCache = ms;
    return _cutoffCache;
}

function isPrescriptionBeforeCutoff(pres) {
    var cutoff = getPrescriptionReadStateCutoffMs();
    if (cutoff === null) return false;
    if (!pres || !pres.created) return false;
    var created = Date.parse(pres.created);
    if (isNaN(created)) return false;
    return created < cutoff;
}

// `viewedIds` (optional) lets a render pass pass in the Set it already built,
// so per-card checks don't re-parse localStorage N times. Omit it to read fresh.
function isPrescriptionUnviewed(pres, viewedIds) {
    if (!isPrescriptionReadStateEnabled()) return false;
    if (!pres || !pres.id) return false;
    if (isPrescriptionBeforeCutoff(pres)) return false;
    var viewed = viewedIds !== undefined ? viewedIds : getViewedPrescriptionIds();
    if (viewed === null) return false;
    return !viewed.has(String(pres.id));
}

// ============================================================================
// Account-page rendering
// ============================================================================

var providedByLabel = document.querySelector('.account-prescriptions__wrapper')?.dataset?.providedByLabel?.trim() || null;

// The prescription-products payload only ever renders these time-of-day
// categories (getPrescriptionWrapper builds a container per key). Appending
// is restricted to this list so a key the payload adds later is knowingly
// excluded here rather than accidentally missed. pro_at_home is the routine
// builder's professional-use bucket, grouped by the API alongside
// am/pm/anytime.
var PRESCRIPTION_CATEGORY_KEYS = ['am', 'pm', 'anytime', 'pro_at_home'];

// HTML-escape clinician-supplied prescription text before interpolating into
// template literals. Title/provider_name/customer_name flow through the Lambda
// from the prescription DB; without escaping, any write-side injection lands
// directly in the patient-account DOM via $(...).append.
function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
}

// The prescription description is clinician-authored rich text (it arrives as
// HTML — <p>, <br>, lists, links). Plain esc() would render those tags as
// literal text ("<p>here is your suggestion:</p>"), and raw interpolation would
// be a stored-XSS hole. Sanitize instead: parse the HTML, then rebuild it
// keeping only an allowlist of formatting tags with NO attributes (except a
// protocol-checked href on <a>), escaping every text node. Anything else
// (scripts, styles, event handlers, unknown tags) is dropped, keeping inner
// text only. Returns a safe HTML string.
var SANITIZE_ALLOWED_TAGS = { P: 1, BR: 1, STRONG: 1, B: 1, EM: 1, I: 1, U: 1, UL: 1, OL: 1, LI: 1, SPAN: 1, A: 1, H3: 1, H4: 1 };

function sanitizeHtml(input) {
    var str = String(input == null ? '' : input);
    if (!str) return '';
    var doc;
    try {
        doc = new DOMParser().parseFromString(str, 'text/html');
    } catch (e) {
        return esc(str);
    }
    function clean(node) {
        var out = '';
        var kids = node.childNodes;
        for (var i = 0; i < kids.length; i++) {
            var child = kids[i];
            if (child.nodeType === 3) {
                out += esc(child.nodeValue);
            } else if (child.nodeType === 1) {
                var tag = child.tagName;
                if (tag === 'SCRIPT' || tag === 'STYLE') continue;
                if (tag === 'BR') { out += '<br>'; continue; }
                var inner = clean(child);
                if (!SANITIZE_ALLOWED_TAGS[tag]) { out += inner; continue; }
                if (tag === 'A') {
                    var href = (child.getAttribute('href') || '').trim();
                    if (/^(https?:|mailto:|\/)/i.test(href)) {
                        out += '<a href="' + esc(href) + '" target="_blank" rel="noopener noreferrer">' + inner + '</a>';
                    } else {
                        out += inner;
                    }
                    continue;
                }
                var t = tag.toLowerCase();
                out += '<' + t + '>' + inner + '</' + t + '>';
            }
        }
        return out;
    }
    return clean(doc.body);
}

// `viewedCtx` (optional) is `{ ids, map }` read once by the caller for the whole
// render pass; when present the per-card read-state helpers reuse it instead of
// re-parsing localStorage per card.
function getPrescriptionWrapper(pres, customer_name, provider_name, presProducts, viewedCtx) {
    var hasAm = presProducts && Array.isArray(presProducts.am) && presProducts.am.length > 0;
    var hasPm = presProducts && Array.isArray(presProducts.pm) && presProducts.pm.length > 0;
    var hasAnytime = presProducts && Array.isArray(presProducts.anytime) && presProducts.anytime.length > 0;
    var hasProAtHome = presProducts && Array.isArray(presProducts.pro_at_home) && presProducts.pro_at_home.length > 0;
    var amSingle = hasAm && presProducts.am.length === 1 ? ' is-single' : '';
    var pmSingle = hasPm && presProducts.pm.length === 1 ? ' is-single' : '';
    var anytimeSingle = hasAnytime && presProducts.anytime.length === 1 ? ' is-single' : '';
    var proAtHomeSingle = hasProAtHome && presProducts.pro_at_home.length === 1 ? ' is-single' : '';
    // When the read-state flag is OFF, render a neutral card with no badge,
    // no "Viewed on" date, and a single CTA. The read-state-driven badge /
    // viewed-on / CTA-state-swap is only meaningful with the flag ON.
    var readStateEnabled = isPrescriptionReadStateEnabled();
    var viewedIds = viewedCtx ? viewedCtx.ids : undefined;
    var viewedMap = viewedCtx ? viewedCtx.map : undefined;
    var unviewed = readStateEnabled && isPrescriptionUnviewed(pres, viewedIds);
    var badgeMarkup = '';
    var viewedOnMarkup = '';
    var ctaMarkup = `<span class="account-prescriptions__view-cta">View recommendations ›</span>`;
    if (readStateEnabled) {
        badgeMarkup = unviewed
            ? `<span class="account-prescriptions__badge account-prescriptions__badge--new">New</span>`
            : `<span class="account-prescriptions__badge account-prescriptions__badge--viewed">Viewed</span>`;
        var viewedTimestamp = !unviewed ? getPrescriptionViewedTimestamp(pres.id, viewedMap) : null;
        if (viewedTimestamp) {
            viewedOnMarkup = `<span class="account-prescriptions__viewed-on">Viewed on ${formatDate(viewedTimestamp)}</span>`;
        }
        ctaMarkup = unviewed
            ? `<span class="account-prescriptions__view-cta">View recommendations ›</span>`
            : `<span class="account-prescriptions__view-cta">View again ›</span>`;
    }
    // Only show the "Hey <name>," greeting block when the description has
    // actual content. Covers null/undefined/empty as well as HTML that renders
    // to nothing (e.g. "<p></p>" or whitespace-only) — stripping the sanitized
    // tags and trimming tells us whether any visible text remains.
    var descHtml = sanitizeHtml(pres.description);
    var subheadingMarkup = descHtml.replace(/<[^>]*>/g, '').trim()
        ? `<div class="account-prescriptions__subheading">Hey ${esc(customer_name)},<br>${descHtml}</div>`
        : '';
    return `<div class="account-prescriptions__item${unviewed ? ' is-unviewed' : ''}" data-pres-id="${esc(pres.id)}">
                    <div class="account-prescriptions__item-header">
                        ${badgeMarkup}
                        ${viewedOnMarkup}
                        <h2 class="account-prescriptions__heading">${esc(pres.title)}</h2>
                        ${providedByLabel && provider_name && provider_name.trim() != '' ? `<p class="account-prescriptions__heading"><span class="account-prescriptions__provided-by">${esc(providedByLabel)} ${esc(provider_name)}</span></p>` : ''}
                        <p class="account-prescriptions__meta"><span class="account-prescriptions__created-at">${formatDate(pres.created)}</span></p>
                        <button class="account-prescriptions__atc-all">
                            <svg width="28" height="29" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M11.3087 7.69274C11.3088 7.5205 11.3959 7.15476 11.7498 6.8271C12.0916 6.51062 12.7531 6.16714 14.0011 6.16714C15.249 6.16714 15.9105 6.51062 16.2523 6.8271C16.6062 7.15476 16.6933 7.5205 16.6934 7.69274C16.6934 7.69275 16.6934 7.69277 16.6934 7.69278V9.51294H11.3087V7.69278C11.3087 7.69277 11.3087 7.69275 11.3087 7.69274ZM9.69336 7.69274C9.69337 7.08719 9.95621 6.28632 10.6523 5.6418C11.3605 4.98605 12.449 4.55176 14.0011 4.55176C15.5531 4.55176 16.6416 4.98605 17.3498 5.6418C18.0459 6.28632 18.3087 7.08719 18.3087 7.69274C18.3087 7.69275 18.3087 7.69277 18.3087 7.69278V9.51294H21.0831C21.5292 9.51294 21.8908 9.87456 21.8908 10.3206V21.3537C21.8908 21.9881 21.6367 22.5951 21.1865 23.0417C20.7365 23.488 20.1276 23.7376 19.4941 23.7376H8.37128C7.73774 23.7376 7.12885 23.488 6.67889 23.0417C6.22872 22.5951 5.97461 21.9881 5.97461 21.3537V10.3206C5.97461 9.87456 6.33623 9.51294 6.7823 9.51294H9.69336V7.69278C9.69336 7.69277 9.69336 7.69275 9.69336 7.69274ZM9.69336 13.5261V11.1283H7.58999V21.3537C7.58999 21.5554 7.6707 21.7502 7.81651 21.8948C7.96254 22.0397 8.16197 22.1222 8.37128 22.1222H19.4941C19.7034 22.1222 19.9029 22.0397 20.0489 21.8948C20.1947 21.7502 20.2754 21.5554 20.2754 21.3537V11.1283H18.3087V13.5261H16.6934V11.1283H11.3087V13.5261H9.69336Z" fill="#121212"/>
                            </svg> add items to cart
                        </button>
                        ${ctaMarkup}
                        ${subheadingMarkup}
                    </div>
                    <div class="account-prescriptions__item-content">
                        <div class="account-prescriptions__products" id="custom-pres-${esc(pres.id)}" data-pres-id="${esc(pres.id)}" data-prescription-date="${esc(pres.created || '')}" data-provider-id="${esc(pres.provider_id || '')}" data-provider-name="${esc(pres.provider_name || '')}" data-provider-clinic-id="${esc(pres.provider_clinic_id || '')}">
                            ${hasAm ? `<div class="account-prescriptions__products--am${amSingle}">
                                <p class="account-prescriptions__product-category">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                        <path d="M12.4984 7.60938C9.68594 7.60938 7.39844 9.89688 7.39844 12.7094C7.39844 15.5219 9.68594 17.8094 12.4984 17.8094C15.3109 17.8094 17.5984 15.5219 17.5984 12.7094C17.5984 9.89688 15.3109 7.60938 12.4984 7.60938ZM12.4984 16.1219C10.6234 16.1219 9.08594 14.5844 9.08594 12.7094C9.08594 10.8344 10.6234 9.29688 12.4984 9.29688C14.3734 9.29688 15.9109 10.8344 15.9109 12.7094C15.9109 14.5844 14.3734 16.1219 12.4984 16.1219Z" fill="black" stroke="black"/>
                                        <path d="M12.4992 4.94766C12.9492 4.94766 13.3617 4.57266 13.3617 4.08516V2.21016C13.3617 1.76016 12.9867 1.34766 12.4992 1.34766C12.0492 1.34766 11.6367 1.72266 11.6367 2.21016V4.12266C11.6742 4.57266 12.0492 4.94766 12.4992 4.94766Z" fill="black" stroke="black"/>
                                        <path d="M12.4992 20.4727C12.0492 20.4727 11.6367 20.8477 11.6367 21.3352V23.2102C11.6367 23.6602 12.0117 24.0727 12.4992 24.0727C12.9492 24.0727 13.3617 23.6977 13.3617 23.2102V21.2977C13.3617 20.8477 12.9492 20.4727 12.4992 20.4727Z" fill="black" stroke="black"/>
                                        <path d="M18.6109 7.45859C18.8359 7.45859 19.0609 7.38359 19.2109 7.19609L20.4109 5.99609C20.7484 5.65859 20.7484 5.13359 20.4109 4.79609C20.0734 4.45859 19.5484 4.45859 19.2109 4.79609L18.0109 5.99609C17.6734 6.33359 17.6734 6.85859 18.0109 7.19609C18.1609 7.38359 18.3859 7.45859 18.6109 7.45859Z" fill="black" stroke="black"/>
                                        <path d="M5.82422 18.2229L4.62422 19.3854C4.28672 19.7229 4.28672 20.2479 4.62422 20.5854C4.77422 20.7354 4.99922 20.8479 5.22422 20.8479C5.44922 20.8479 5.67422 20.7729 5.82422 20.5854L7.02422 19.3854C7.36172 19.0479 7.36172 18.5229 7.02422 18.1854C6.68672 17.8854 6.12422 17.8854 5.82422 18.2229Z" fill="black" stroke="black"/>
                                        <path d="M22.9977 11.8867H21.0852C20.6352 11.8867 20.2227 12.2617 20.2227 12.7492C20.2227 13.1992 20.5977 13.6117 21.0852 13.6117H22.9977C23.4477 13.6117 23.8602 13.2367 23.8602 12.7492C23.8602 12.2617 23.4477 11.8867 22.9977 11.8867Z" fill="black" stroke="black"/>
                                        <path d="M4.73672 12.7102C4.73672 12.2602 4.36172 11.8477 3.87422 11.8477H1.99922C1.54922 11.8477 1.13672 12.2227 1.13672 12.7102C1.13672 13.1602 1.51172 13.5727 1.99922 13.5727H3.91172C4.36172 13.5727 4.73672 13.1602 4.73672 12.7102Z" fill="black" stroke="black"/>
                                        <path d="M19.1758 18.2219C18.8383 17.8844 18.3133 17.8844 17.9758 18.2219C17.6383 18.5594 17.6383 19.0844 17.9758 19.4219L19.1758 20.6219C19.3258 20.7719 19.5508 20.8844 19.7758 20.8844C20.0008 20.8844 20.2258 20.8094 20.3758 20.6219C20.7133 20.2844 20.7133 19.7594 20.3758 19.4219L19.1758 18.2219Z" fill="black" stroke="black"/>
                                        <path d="M5.82422 4.83516C5.48672 4.49766 4.96172 4.49766 4.62422 4.83516C4.28672 5.17266 4.28672 5.69766 4.62422 6.03516L5.82422 7.23516C5.97422 7.38516 6.19922 7.49766 6.42422 7.49766C6.64922 7.49766 6.87422 7.42266 7.02422 7.23516C7.36172 6.89766 7.36172 6.37266 7.02422 6.03516L5.82422 4.83516Z" fill="black" stroke="black"/>
                                    </svg>
                                    AM
                                </p>
                            </div>` : ''}
                            ${hasPm ? `<div class="account-prescriptions__products--pm${pmSingle}">
                                <p class="account-prescriptions__product-category">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                        <path d="M13.8115 2.21271C13.174 2.02521 12.5365 1.87521 11.8615 1.76271C11.374 1.68771 10.849 1.95021 10.6615 2.40021C10.4365 2.85021 10.549 3.41271 10.924 3.71271C13.5865 6.18771 14.5615 9.82521 13.474 13.2377C12.274 17.0252 8.74897 19.6877 4.66147 19.8002C4.13647 19.8002 3.72397 20.1377 3.57397 20.6252C3.42397 21.1127 3.64897 21.6377 4.06147 21.9002C4.99897 22.4627 5.93647 22.9127 6.87397 23.2127C7.96147 23.5502 9.08647 23.7002 10.2115 23.7002C12.124 23.7002 13.999 23.2127 15.6865 22.2752C18.349 20.8127 20.224 18.4502 21.049 15.6002C22.6615 9.97521 19.474 4.08771 13.8115 2.21271ZM19.4365 15.1127C18.7615 17.5502 17.1115 19.5377 14.899 20.7752C12.574 22.0502 9.91147 22.3127 7.39897 21.5627C7.13647 21.4877 6.83647 21.3752 6.57397 21.2627C10.549 20.4752 13.849 17.6252 15.0865 13.7252C16.174 10.2752 15.499 6.63771 13.2865 3.78771C18.0865 5.40021 20.7865 10.3877 19.4365 15.1127Z" fill="black"/>
                                    </svg>
                                    PM
                                </p>
                            </div>` : ''}
                            ${hasAnytime ? `<div class="account-prescriptions__products--anytime${anytimeSingle}">
                                <p class="account-prescriptions__product-category">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                        <path d="M12.4984 7.60938C9.68594 7.60938 7.39844 9.89688 7.39844 12.7094C7.39844 15.5219 9.68594 17.8094 12.4984 17.8094C15.3109 17.8094 17.5984 15.5219 17.5984 12.7094C17.5984 9.89688 15.3109 7.60938 12.4984 7.60938ZM12.4984 16.1219C10.6234 16.1219 9.08594 14.5844 9.08594 12.7094C9.08594 10.8344 10.6234 9.29688 12.4984 9.29688C14.3734 9.29688 15.9109 10.8344 15.9109 12.7094C15.9109 14.5844 14.3734 16.1219 12.4984 16.1219Z" fill="black" stroke="black"/>
                                        <path d="M12.4992 4.94766C12.9492 4.94766 13.3617 4.57266 13.3617 4.08516V2.21016C13.3617 1.76016 12.9867 1.34766 12.4992 1.34766C12.0492 1.34766 11.6367 1.72266 11.6367 2.21016V4.12266C11.6742 4.57266 12.0492 4.94766 12.4992 4.94766Z" fill="black" stroke="black"/>
                                        <path d="M12.4992 20.4727C12.0492 20.4727 11.6367 20.8477 11.6367 21.3352V23.2102C11.6367 23.6602 12.0117 24.0727 12.4992 24.0727C12.9492 24.0727 13.3617 23.6977 13.3617 23.2102V21.2977C13.3617 20.8477 12.9492 20.4727 12.4992 20.4727Z" fill="black" stroke="black"/>
                                        <path d="M18.6109 7.45859C18.8359 7.45859 19.0609 7.38359 19.2109 7.19609L20.4109 5.99609C20.7484 5.65859 20.7484 5.13359 20.4109 4.79609C20.0734 4.45859 19.5484 4.45859 19.2109 4.79609L18.0109 5.99609C17.6734 6.33359 17.6734 6.85859 18.0109 7.19609C18.1609 7.38359 18.3859 7.45859 18.6109 7.45859Z" fill="black" stroke="black"/>
                                        <path d="M5.82422 18.2229L4.62422 19.3854C4.28672 19.7229 4.28672 20.2479 4.62422 20.5854C4.77422 20.7354 4.99922 20.8479 5.22422 20.8479C5.44922 20.8479 5.67422 20.7729 5.82422 20.5854L7.02422 19.3854C7.36172 19.0479 7.36172 18.5229 7.02422 18.1854C6.68672 17.8854 6.12422 17.8854 5.82422 18.2229Z" fill="black" stroke="black"/>
                                        <path d="M22.9977 11.8867H21.0852C20.6352 11.8867 20.2227 12.2617 20.2227 12.7492C20.2227 13.1992 20.5977 13.6117 21.0852 13.6117H22.9977C23.4477 13.6117 23.8602 13.2367 23.8602 12.7492C23.8602 12.2617 23.4477 11.8867 22.9977 11.8867Z" fill="black" stroke="black"/>
                                        <path d="M4.73672 12.7102C4.73672 12.2602 4.36172 11.8477 3.87422 11.8477H1.99922C1.54922 11.8477 1.13672 12.2227 1.13672 12.7102C1.13672 13.1602 1.51172 13.5727 1.99922 13.5727H3.91172C4.36172 13.5727 4.73672 13.1602 4.73672 12.7102Z" fill="black" stroke="black"/>
                                        <path d="M19.1758 18.2219C18.8383 17.8844 18.3133 17.8844 17.9758 18.2219C17.6383 18.5594 17.6383 19.0844 17.9758 19.4219L19.1758 20.6219C19.3258 20.7719 19.5508 20.8844 19.7758 20.8844C20.0008 20.8844 20.2258 20.8094 20.3758 20.6219C20.7133 20.2844 20.7133 19.7594 20.3758 19.4219L19.1758 18.2219Z" fill="black" stroke="black"/>
                                        <path d="M5.82422 4.83516C5.48672 4.49766 4.96172 4.49766 4.62422 4.83516C4.28672 5.17266 4.28672 5.69766 4.62422 6.03516L5.82422 7.23516C5.97422 7.38516 6.19922 7.49766 6.42422 7.49766C6.64922 7.49766 6.87422 7.42266 7.02422 7.23516C7.36172 6.89766 7.36172 6.37266 7.02422 6.03516L5.82422 4.83516Z" fill="black" stroke="black"/>
                                    </svg>
                                    AM /
                                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                        <path d="M13.8115 2.21271C13.174 2.02521 12.5365 1.87521 11.8615 1.76271C11.374 1.68771 10.849 1.95021 10.6615 2.40021C10.4365 2.85021 10.549 3.41271 10.924 3.71271C13.5865 6.18771 14.5615 9.82521 13.474 13.2377C12.274 17.0252 8.74897 19.6877 4.66147 19.8002C4.13647 19.8002 3.72397 20.1377 3.57397 20.6252C3.42397 21.1127 3.64897 21.6377 4.06147 21.9002C4.99897 22.4627 5.93647 22.9127 6.87397 23.2127C7.96147 23.5502 9.08647 23.7002 10.2115 23.7002C12.124 23.7002 13.999 23.2127 15.6865 22.2752C18.349 20.8127 20.224 18.4502 21.049 15.6002C22.6615 9.97521 19.474 4.08771 13.8115 2.21271ZM19.4365 15.1127C18.7615 17.5502 17.1115 19.5377 14.899 20.7752C12.574 22.0502 9.91147 22.3127 7.39897 21.5627C7.13647 21.4877 6.83647 21.3752 6.57397 21.2627C10.549 20.4752 13.849 17.6252 15.0865 13.7252C16.174 10.2752 15.499 6.63771 13.2865 3.78771C18.0865 5.40021 20.7865 10.3877 19.4365 15.1127Z" fill="black"/>
                                    </svg>
                                    PM
                                </p>
                            </div>` : ''}
                            ${hasProAtHome ? `<div class="account-prescriptions__products--pro_at_home${proAtHomeSingle}">
                                <p class="account-prescriptions__product-category">
                                    PRO AT HOME
                                </p>
                            </div>` : ''}
                        </div>
                    </div>
                </div>`;
}

function formatDate(dateString) {
    var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    var date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    var day = date.getDate();
    var dayPostFix = day == 1 ? "st" : day == 2 ? "nd" : day == 3 ? "rd" : "th";
    return `${months[date.getMonth()]} ${day}${dayPostFix}, ${date.getFullYear()}`;
}

/** Numeric Shopify variant id for ?variant= (from GID, number string, or embedded product snapshot). */
function prescriptionVariantNumericId(raw) {
    if (raw == null || raw === "") return "";
    var s = String(raw);
    var m = s.match(/ProductVariant\/(\d+)/i);
    if (m) return m[1];
    if (/^\d+$/.test(s)) return s;
    return "";
}

/**
 * Resolve variant id from a prescription line object (Connect/API shapes).
 * Prefers explicit variant fields and embedded Storefront variant before top-level id
 * (top-level id may be product GID on legacy rows).
 */
function prescriptionVariantIdForShopifyUrl(presProduct) {
    if (!presProduct || typeof presProduct !== "object") return "";
    var candidates = [];
    if (presProduct.variantId) candidates.push(presProduct.variantId);
    if (presProduct.variant_id) candidates.push(presProduct.variant_id);
    var edges =
        presProduct.product &&
        presProduct.product.variants &&
        presProduct.product.variants.edges;
    if (edges && edges.length && edges[0].node && edges[0].node.id) {
        // No explicit variant id -- first edge is best-effort only.
        candidates.push(edges[0].node.id);
    }
    if (presProduct.id) candidates.push(presProduct.id);
    for (var i = 0; i < candidates.length; i++) {
        var num = prescriptionVariantNumericId(candidates[i]);
        if (num) return num;
    }
    return "";
}

/**
 * Fetches one product's prescription-view markup and replaces its reserved slot with it.
 *
 * Replacing a slot rather than appending on arrival is what holds the order — see the
 * call site in renderList, which reserves the slots. A product that cannot be loaded has
 * its slot removed, closing the gap instead of leaving a hole in the routine.
 *
 * @param {string} shop_url          Storefront origin.
 * @param {string} handle            Product handle to fetch.
 * @param {jQuery} $container        The category container, already in the document.
 * @param {string} type              Category key, stamped onto the card as `pres-type`.
 * @param {string} variantNumericId  Numeric variant id, or "" to let Liquid choose.
 * @param {jQuery} slot              Placeholder to replace. Optional in the signature only:
 *                                   without it the card appends at the end, out of order.
 */
function getPrescriptionProduct(shop_url, handle, $container, type, variantNumericId, slot) {
    var qs = new URLSearchParams({ view: "prescription" });
    if (variantNumericId) qs.set("variant", String(variantNumericId));
    fetch(`${shop_url}/products/${handle}?${qs.toString()}`)
        .then(response => {
            // A handle that has been deleted, unpublished or renamed answers 404 with a
            // body, which fetch reports as a resolved promise. Without this the failure
            // takes the success path below and never reaches the catch.
            if (!response.ok) throw new Error('HTTP ' + response.status + ' for ' + handle);
            return response.text();
        })
        .then(html => {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            $(doc).find(".account-prescriptions__product-item").attr("pres-type", type);
            var getHtml = $(doc).find(".account-prescriptions__html").html();

            // A 2xx page that carries no prescription markup — the product renders, but
            // not through templates/product.prescription.liquid. Returning here also
            // stops the category being revealed with a heading and no cards.
            if (!getHtml) {
                console.warn('[prescription.js] no prescription markup for handle "' + handle + '"');
                if (slot && slot.length) slot.remove();
                return;
            }

            if (slot && slot.length) {
                slot.replaceWith(getHtml);
            } else {
                // No slot reserved (defensive): fall back to the old append so a product is
                // still rendered rather than dropped. Note this appends at the end, so it
                // renders out of order — the very thing the slots exist to prevent.
                console.warn('[prescription.js] no slot for "' + handle + '" — appending out of order');
                $container.append(getHtml);
            }
            $container.addClass("active");
        })
        .catch((error) => {
            if (slot && slot.length) slot.remove();
            console.error('Error:', error);
        });
}

function openPrescriptionFromUrl() {
    var presId = new URLSearchParams(window.location.search).get("prescription");
    if (!presId) return;
    presId = presId.replace(/\/+$/, "");
    if (!/^[\w-]+$/.test(presId)) return;
    var $item = $('.account-prescriptions__item[data-pres-id="' + presId + '"]');
    if (!$item.length || $item.hasClass("active")) return;
    $item.find(".account-prescriptions__item-header").first().trigger("click");
    var offset = $item.offset();
    if (offset) {
        $("html, body").animate({ scrollTop: offset.top - 100 }, 300);
    }
}

function updatePrescriptions() {
    var template = ($("body").attr("data-template") || "").trim();
    if (template != 'customers/account') return;

    var customer_email = ($("body").attr("data-customer-email") || "").trim();
    if (!customer_email) return;

    var customer_name = ($("body").attr("data-customer-name") || "").trim();
    var shop_url = ($("body").attr("data-shop-url") || "").trim();

    function renderEmpty() {
        $(".account-prescriptions__wrapper").html(`<p class="account-prescriptions__empty">No recommendations right now!</p>`);
    }

    function renderList(list) {
        if (!list || list.length === 0) {
            renderEmpty();
            return;
        }
        // Read the viewed-state once for the whole render pass so per-card
        // read-state helpers don't re-parse localStorage N times.
        var rsEnabled = isPrescriptionReadStateEnabled();
        var vMap = rsEnabled ? getViewedPrescriptionMap() : null;
        var viewedCtx = {
            ids: rsEnabled ? (vMap === null ? null : new Set(Object.keys(vMap))) : null,
            map: vMap
        };
        for (var i = 0; i < list.length; i++) {
            var pres = list[i];
            var presProducts;
            try {
                presProducts = JSON.parse(pres.products);
            } catch (e) {
                console.warn('[prescription.js] Skipping prescription ' + pres.id + ': invalid products JSON');
                continue;
            }
            // Held as an element rather than re-derived from an `#custom-pres-${pres.id}`
            // selector below. That string is interpolated from server data and is parsed
            // synchronously here, so a character Sizzle rejects would throw out of
            // renderList — and both callers answer that by calling renderEmpty(), wiping
            // the whole panel after some cards have already rendered. Scoping to the
            // element also keeps the right container if two rows ever share an id.
            var $wrapper = $(getPrescriptionWrapper(pres, customer_name, pres.provider_name, presProducts, viewedCtx));
            $(".account-prescriptions__wrapper").append($wrapper);
            // Walk the known category keys rather than the payload's own, so a key added
            // upstream is knowingly excluded here. Array.isArray guards a key whose value
            // drifted to a non-array; the empty-container check below covers a category
            // whose container getPrescriptionWrapper did not render.
            for (var c = 0; c < PRESCRIPTION_CATEGORY_KEYS.length; c++) {
                var catKey = PRESCRIPTION_CATEGORY_KEYS[c];
                var presProductArr = presProducts[catKey];
                if (Array.isArray(presProductArr) && presProductArr.length > 0) {
                    var $container = $wrapper.find(".account-prescriptions__products--" + catKey);
                    if (!$container.length) continue;
                    // Reserve a slot per product up front, in payload order, then let the
                    // fetches fill them as they land. The cards are fetched in parallel and
                    // resolve in whatever order the network returns, so appending on arrival
                    // ordered the routine by response time. Payload order is the order the
                    // clinician arranged in CONNECT — the order a patient applies products
                    // in — and it is preserved end to end, with no sort on the read path.
                    // The cart depends on it too: .account-prescriptions__atc-all builds its
                    // line items by walking these elements in DOM order.
                    for (var presProduct of presProductArr) {
                        var vId = prescriptionVariantIdForShopifyUrl(presProduct);
                        var $slot = $('<div class="account-prescriptions__product-slot"></div>').appendTo($container);
                        getPrescriptionProduct(shop_url, presProduct.handle, $container, catKey, vId, $slot);
                    }
                }
            }
        }
        openPrescriptionFromUrl();
    }

    // Cache hit: render immediately, no fetch. Shares the same 5-min TTL
    // sessionStorage entry used by the header-dot IIFE. This dedupes warm
    // navigations; on a cold cache, an in-flight fetch is shared via
    // getPrescriptionsListPromise() so /account fires a single Lambda call.
    var cached = readCachedPrescriptionsList();
    if (cached) {
        try {
            renderList(cached);
        } catch (e) {
            renderEmpty();
            console.error('Error:', e);
        }
        return;
    }

    getPrescriptionsListPromise(customer_email)
        .then(list => {
            if (Array.isArray(list)) {
                renderList(list);
            } else {
                renderEmpty();
            }
        })
        .catch((error) => {
            renderEmpty();
            console.error('Error:', error);
        });
}

// ============================================================================
// Click handlers
// ============================================================================

$(document).on("click", ".account-prescriptions__item-header", function (e) {
    if ($(e.target).closest(".account-prescriptions__atc-all").length) return;
    var $header = $(this);
    var item = $header.parents(".account-prescriptions__item");

    var willExpand = !item.hasClass("active");
    var presId = item.attr("data-pres-id");
    var $badge = $header.find(".account-prescriptions__badge--new");

    // Mark-as-viewed on any expand transition that has a "New" badge — including
    // auto-expand via openPrescriptionFromUrl(). "Opened/expanded" is the trigger.
    if (
        willExpand &&
        $badge.length > 0 &&
        presId &&
        isPrescriptionReadStateEnabled()
    ) {
        $badge.removeClass("account-prescriptions__badge--new")
              .addClass("account-prescriptions__badge--viewed")
              .text("Viewed");
        $header.find(".account-prescriptions__view-cta").text("View again ›");
        item.removeClass("is-unviewed");
        recordPrescriptionViewed(presId);
        var nowFormatted = formatDate(new Date().toISOString());
        $badge.after(`<span class="account-prescriptions__viewed-on">Viewed on ${nowFormatted}</span>`);

        var remaining = $(".account-prescriptions__wrapper .account-prescriptions__badge--new").length;
        if (remaining === 0) {
            document.dispatchEvent(new CustomEvent("prescriptionsAllViewed"));
        }
    }

    item.toggleClass("active");
    $header.next(".account-prescriptions__item-content").toggle();
    var url = new URL(window.location);
    if (item.hasClass("active") && presId) {
        url.searchParams.set("prescription", presId);
    } else {
        url.searchParams.delete("prescription");
    }
    window.history.replaceState(null, "", url);
});

$(document).on("click", ".account-prescriptions__atc-all", function () {
    var arr = [];
    // A prescription can legitimately list the same product more than once, and
    // the list shows every occurrence. The cart should not: /cart/add.js sums
    // entries that share a variant and properties into one line item, so
    // sending each occurrence lands the patient on quantity 2+. Only the first
    // occurrence of a variant is sent.
    //
    // Keyed on the variant id rather than the product, so two sizes of the same
    // product still add as separate lines. The `return` below is a continue —
    // jQuery reads `return false` from an .each() callback as a break, which
    // here would drop every product after the first duplicate.
    var seenVariantIds = [];
    $(this).parents(".account-prescriptions__item").find(".account-prescriptions__product-item").each(function () {
        if (!$(this).hasClass("sold")) {
            var variantId = $(this).attr("data-id");
            if (seenVariantIds.indexOf(variantId) !== -1) return;
            seenVariantIds.push(variantId);

            var presId = $(this).parents(".account-prescriptions__products").attr("data-pres-id");
            var prescriptionDate = $(this).parents(".account-prescriptions__products").attr("data-prescription-date");
            var patientEmail = $(this).attr("data-email");
            var providerId = $(this).parents(".account-prescriptions__products").attr("data-provider-id");
            var providerName = $(this).parents(".account-prescriptions__products").attr("data-provider-name");
            var providerClinicId = $(this).parents(".account-prescriptions__products").attr("data-provider-clinic-id");
            arr.push({ 'id': variantId, 'quantity': 1, properties: { '_patient_email': patientEmail, '_prescription_id': presId, '_prescription_date': prescriptionDate, '_provider_id': providerId, '_provider_name': providerName, '_provider_clinic_id': providerClinicId } });
        }
    });
    // Nothing addable — every product is sold out, or the cards have not
    // finished loading. Posting an empty batch would still open the cart
    // drawer, which reads as a successful add.
    if (!arr.length) return;
    AddQuizProductsToCart(arr, $(this));
});

$(document).on("click", ".account-prescriptions__product-submit", function () {
    var arr = [];
    var variantId = $(this).attr("data-id");
    var presId = $(this).parents(".account-prescriptions__products").attr("data-pres-id");
    var prescriptionDate = $(this).parents(".account-prescriptions__products").attr("data-prescription-date");
    var patientEmail = $(this).attr("data-email");
    var providerId = $(this).parents(".account-prescriptions__products").attr("data-provider-id");
    var providerName = $(this).parents(".account-prescriptions__products").attr("data-provider-name");
    var providerClinicId = $(this).parents(".account-prescriptions__products").attr("data-provider-clinic-id");
    arr.push({ 'id': variantId, 'quantity': 1, properties: { '_patient_email': patientEmail, '_prescription_id': presId, '_prescription_date': prescriptionDate, '_provider_id': providerId, '_provider_name': providerName, '_provider_clinic_id': providerClinicId } });
    AddQuizProductsToCart(arr, $(this));
});

// ============================================================================
// Header notification dot
// ============================================================================
// Lit when at least one prescription is unviewed (or, if the read-state flag
// is off, lit when any prescription exists — preserving legacy behavior).
// Listens for `prescriptionsAllViewed` to dismiss in-place when the patient
// marks the last unread on the account page in the same session.

(function () {
    // data-customer-email is url-encoded by Liquid. Use it as-is in the query string;
    // do NOT apply encodeURIComponent (that would double-encode and the Lambda patient
    // lookup would miss).
    var customerEmail = document.body.getAttribute('data-customer-email');
    if (!customerEmail) return;

    var readStateEnabled = document.body.getAttribute('data-prescription-read-state-enabled') === 'true';

    document.addEventListener('prescriptionsAllViewed', function () {
        var dots = document.querySelectorAll('.header__prescription-dot-wrapper');
        for (var i = 0; i < dots.length; i++) {
            dots[i].classList.remove('active');
        }
    });

    function applyShouldLight(list) {
        if (list.length === 0) return;
        var shouldLight;
        if (readStateEnabled) {
            var viewed = getViewedPrescriptionIds();
            if (viewed === null) {
                // localStorage unavailable — degrade to "all viewed" rather than spam a stuck dot.
                shouldLight = false;
            } else {
                shouldLight = list.some(function (p) {
                    return p && p.id != null && !isPrescriptionBeforeCutoff(p) && !viewed.has(String(p.id));
                });
            }
        } else {
            shouldLight = true;
        }
        if (!shouldLight) return;
        var dots = document.querySelectorAll('.header__prescription-dot-wrapper');
        for (var i = 0; i < dots.length; i++) {
            dots[i].classList.add('active');
        }
    }

    var cached = readCachedPrescriptionsList();
    if (cached) {
        applyShouldLight(cached);
        return;
    }

    getPrescriptionsListPromise(customerEmail)
        .then(function (list) {
            if (Array.isArray(list)) applyShouldLight(list);
        })
        .catch(function () {});
})();

// ============================================================================
// Bootstrap
// ============================================================================

updatePrescriptions();
})();
